import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, CheckCircle2, Circle, RotateCcw } from "lucide-react";
import EnrichmentModal from "@/pages/manchester/EnrichmentModal";
import ChecklistDownloadModal from "@/pages/resources/ChecklistDownloadModal";
import AdminResourceTracker from "@/components/AdminResourceTracker";
import { base44 } from "@/api/base44Client";

const CHECKLIST = [
  {
    category: "Certificates",
    required: true,
    items: [
      "Electrical Safety Certificate (EICR)",
      "Gas Safety Certificate (if there's gas)",
      "Fire Risk Assessment",
    ],
  },
  {
    category: "General Essentials",
    required: true,
    items: [
      "WiFi (high speed)",
      "3 sets of keys for 1 bed property",
      "4 sets of keys for +2 bed property",
      "Bulbs for all lights",
    ],
  },
  {
    category: "Safety",
    required: true,
    items: [
      "Smoke alarm",
      "Carbon monoxide alarm (if there's gas)",
      "Fire escape plan displayed (we can help)",
    ],
  },
  {
    category: "Living & Dining",
    required: true,
    items: [
      "Smart TV",
      "Comfortable seating",
      "Dining table or seated meal area",
      "Dining chairs (enough for all guests)",
    ],
  },
  {
    category: "Cleaning",
    required: true,
    items: [
      "Vacuum",
      "Mop & bucket",
      "Duster",
      "Waste & recycling bins",
      "Cleaning cloths",
      "Broom, dustpan + brush",
      "Iron + ironing board",
    ],
  },
  {
    category: "Kitchen",
    required: true,
    items: [
      "Small appliances (kettle, toaster)",
      "Large appliances (oven, stove, fridge)",
      "Cutlery (at least 2 per guest)",
      "Crockery (at least 2 per guest)",
      "Mugs & glasses (2 each per guest)",
      "Wine glasses (at least 2 per guest)",
      "Cooking equipment (pots, pans etc.)",
      "Oven trays & dishes",
      "Can opener",
      "Bottle opener & corkscrew",
      "Fire blanket",
      "Knife set",
      "Chopping boards",
      "Cooking utensils",
      "Kitchen bin",
    ],
  },
  {
    category: "Bathroom",
    required: true,
    items: [
      "Toilet brush",
      "Toilet roll holder",
      "Small bathroom bin",
    ],
  },
  {
    category: "Bedroom",
    required: true,
    items: [
      "Pillows (2 per person)",
      "Duvet (inc. sofa beds)",
      "Mattress protectors",
      "Clothes storage",
      "Hangers (10 per bedroom)",
    ],
  },
  {
    category: "Highly Recommended",
    required: false,
    items: [
      "Mirror",
      "Coffee machine",
      "Washing machine & dryer",
      "Hair dryer",
      "Extra blankets + throw pillows",
      "Artwork, decor & plants (fake/real)",
      "Bedside tables",
      "Lamps for softer lighting",
      "Dishwasher",
      "Microwave",
      "Desk & chair",
    ],
  },
];

// Build initial state: { "category|item": false }
function buildInitialState() {
  const state = {};
  CHECKLIST.forEach((cat) => {
    cat.items.forEach((item) => {
      state[`${cat.category}|${item}`] = false;
    });
  });
  return state;
}

function countChecked(checked) {
  return Object.values(checked).filter(Boolean).length;
}
function countTotal(checked) {
  return Object.keys(checked).length;
}

export default function ShortLetSetupChecklist() {
  const [checked, setChecked] = useState(buildInitialState);
  const [showModal, setShowModal] = useState(false);
  const [showDownloadGate, setShowDownloadGate] = useState(false);
  const slug = "short-let-setup-checklist";

  useEffect(() => {
    const pageUrl = `https://www.truestays.co.uk/resources/${slug}`;
    const title = "Short-Let & Airbnb Setup Checklist (Free Interactive Checklist) | Truestays";
    const description = "The complete room-by-room short-let and Airbnb setup checklist for UK landlords. Tick off every essential item interactively, then download the free PDF version.";
    const image = "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/732327359_generated_image.png";

    document.title = title;

    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.name = name; document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setProperty = (property, content) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("property", property); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    setMeta("robots", "index, follow");
    setMeta("keywords", "short-let checklist, airbnb setup checklist, serviced accommodation checklist, airbnb ready checklist UK, furnishing checklist landlords");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", image);

    setProperty("og:title", title);
    setProperty("og:description", description);
    setProperty("og:type", "article");
    setProperty("og:url", pageUrl);
    setProperty("og:image", image);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = pageUrl;

    // Structured data (HowTo) for rich results
    let ld = document.getElementById("checklist-jsonld");
    if (!ld) { ld = document.createElement("script"); ld.id = "checklist-jsonld"; ld.type = "application/ld+json"; document.head.appendChild(ld); }
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: "Short-Let & Airbnb Setup Checklist",
      description,
      image,
      step: CHECKLIST.map((cat) => ({
        "@type": "HowToSection",
        name: cat.category,
        itemListElement: cat.items.map((item) => ({ "@type": "HowToDirection", text: item })),
      })),
    });

    return () => { ld?.remove(); };
  }, []);

  const toggle = (key) => setChecked((prev) => ({ ...prev, [key]: !prev[key] }));
  const reset = () => setChecked(buildInitialState());

  const totalChecked = countChecked(checked);
  const total = countTotal(checked);
  const progress = Math.round((totalChecked / total) * 100);

  const handleDownload = () => setShowDownloadGate(true);

  return (
    <div>
      <AdminResourceTracker page={`resource:${slug}`} />

      {/* Hero */}
      <div style={{ backgroundColor: "#161616", paddingTop: "clamp(108px, 9vw, 140px)", paddingBottom: "clamp(40px, 5vw, 56px)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", paddingLeft: "clamp(20px, 5vw, 80px)", paddingRight: "clamp(20px, 5vw, 80px)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "clamp(24px, 3vw, 40px)" }}>
            <Link to="/resources" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "rgba(255,255,255,0.6)", textDecoration: "none", display: "flex", alignItems: "center", gap: 4 }}>
              <ArrowLeft size={13} /> Resources
            </Link>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 32, alignItems: "center" }} className="checklist-hero-grid">
            <div>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", color: "rgba(255,255,255,0.5)", textTransform: "uppercase", marginBottom: 12, marginTop: 0 }}>Guide · Checklist</p>
              <h1 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "clamp(28px, 4vw, 46px)", lineHeight: 1.12, letterSpacing: "-0.03em", color: "#fff", marginTop: 0, marginBottom: 16 }}>Short-let essentials checklist</h1>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "rgba(255,255,255,0.6)", margin: "0 0 24px", lineHeight: 1.6, maxWidth: 520 }}>
                Everything your property needs before welcoming its first guests. Work through it interactively, or download the Truestays branded PDF version.
              </p>
              <button
                onClick={handleDownload}
                style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "hsl(var(--primary))", color: "#fff", border: "none", borderRadius: 8, padding: "12px 22px", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", transition: "background 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.background = "#C4C024"}
                onMouseLeave={e => e.currentTarget.style.background = "hsl(var(--primary))"}
              >
                <Download size={15} /> Download PDF
              </button>
            </div>
            <div className="checklist-progress-hero" style={{ textAlign: "center", flexShrink: 0 }}>
              <div style={{ width: 110, height: 110, borderRadius: "50%", background: "rgba(255,255,255,0.06)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", border: "2px solid rgba(255,255,255,0.12)" }}>
                <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 28, color: "#fff", lineHeight: 1 }}>{progress}%</span>
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "rgba(255,255,255,0.5)", marginTop: 4 }}>complete</span>
              </div>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "rgba(255,255,255,0.45)", margin: "10px 0 0" }}>{totalChecked} of {total} items</p>
            </div>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ background: "#F7F7F7", borderBottom: "1px solid #E5E5E5", padding: "14px clamp(20px, 5vw, 80px)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ flex: 1, height: 6, background: "#E5E5E5", borderRadius: 100, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${progress}%`, background: "hsl(var(--primary))", borderRadius: 100, transition: "width 0.3s ease" }} />
          </div>
          <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#888", whiteSpace: "nowrap" }}>{totalChecked}/{total} checked</span>
          <button onClick={reset} style={{ display: "flex", alignItems: "center", gap: 5, fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#888", background: "none", border: "none", cursor: "pointer", padding: 0, transition: "color 0.2s" }}
            onMouseEnter={e => e.currentTarget.style.color = "#333"}
            onMouseLeave={e => e.currentTarget.style.color = "#888"}>
            <RotateCcw size={12} /> Reset
          </button>
        </div>
      </div>

      {/* Checklist body */}
      <div style={{ backgroundColor: "#fff", paddingTop: "clamp(32px, 5vw, 56px)", paddingBottom: "clamp(60px, 7vw, 96px)", paddingLeft: "clamp(20px, 5vw, 80px)", paddingRight: "clamp(20px, 5vw, 80px)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>

          {/* Intro note */}
          <div style={{ background: "#E8EDFF", borderRadius: 12, padding: "16px 20px", marginBottom: 40, display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div style={{ flexShrink: 0, marginTop: 2 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "hsl(var(--primary))", marginTop: 5 }} />
            </div>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#555", margin: 0, lineHeight: 1.65 }}>
              <strong style={{ color: "#1A1A1A" }}>To help prepare your home for your first guests,</strong> please ensure the property includes all of the items listed below as a minimum standard prior to the onboarding date. This is only the baseline — we strongly recommend going beyond this wherever possible.
            </p>
          </div>

          {/* Grid of categories */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 24 }} className="checklist-grid">
            {CHECKLIST.map((cat) => {
              const catChecked = cat.items.filter((item) => checked[`${cat.category}|${item}`]).length;
              const catTotal = cat.items.length;
              const allDone = catChecked === catTotal;

              return (
                <div
                  key={cat.category}
                  style={{
                    border: `1px solid ${allDone ? "#D1FADF" : "#E5E5E5"}`,
                    borderRadius: 16,
                    overflow: "hidden",
                    transition: "border-color 0.3s",
                    background: allDone ? "#F6FEF9" : "#fff",
                  }}
                >
                  {/* Category header */}
                  <div style={{
                    padding: "14px 20px",
                    borderBottom: `1px solid ${allDone ? "#D1FADF" : "#E5E5E5"}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: allDone ? "#ECFDF5" : cat.required ? "#fff" : "#FFFBF0",
                    transition: "background 0.3s",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 14, color: "#1A1A1A", margin: 0 }}>{cat.category}</h3>
                      {!cat.required && (
                        <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, color: "#B45309", background: "#FEF3C7", borderRadius: 100, padding: "2px 8px" }}>Optional</span>
                      )}
                    </div>
                    <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: allDone ? "#059669" : "#888", fontWeight: allDone ? 700 : 400 }}>
                      {catChecked}/{catTotal}
                    </span>
                  </div>

                  {/* Items */}
                  <div style={{ padding: "8px 0" }}>
                    {cat.items.map((item) => {
                      const key = `${cat.category}|${item}`;
                      const isChecked = checked[key];
                      return (
                        <button
                          key={item}
                          onClick={() => toggle(key)}
                          style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            padding: "9px 20px",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            textAlign: "left",
                            transition: "background 0.15s",
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = "#F7F7F7"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                        >
                          {isChecked
                            ? <CheckCircle2 size={18} color="#059669" strokeWidth={2} style={{ flexShrink: 0 }} />
                            : <Circle size={18} color="#CCCCCC" strokeWidth={1.5} style={{ flexShrink: 0 }} />
                          }
                          <span style={{
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: 14,
                            color: isChecked ? "#888" : "#1A3A2A",
                            textDecoration: isChecked ? "line-through" : "none",
                            transition: "color 0.2s, text-decoration 0.2s",
                            lineHeight: 1.4,
                          }}>{item}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Linen note */}
          <div style={{ marginTop: 32, background: "#F7F7F7", borderRadius: 14, padding: "20px 24px", display: "flex", alignItems: "flex-start", gap: 14 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: "#E5E5E5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <span style={{ fontSize: 18 }}>🛏</span>
            </div>
            <div>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: 14, color: "#1A1A1A", margin: "0 0 4px" }}>Hotel quality linen & towels are handled by us</p>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#555", margin: 0, lineHeight: 1.6 }}>We organise hotel quality linen & towels for you, so no need to purchase these yourself.</p>
            </div>
          </div>

          {/* Bottom CTA */}
          <div style={{ marginTop: 48, background: "#F7F7F7", borderRadius: 14, padding: "32px 28px", display: "flex", flexDirection: "column", gap: 12 }}>
            <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 20, color: "#1A1A1A", margin: 0 }}>Ready to get your property live?</p>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#555", margin: 0, lineHeight: 1.6 }}>Get a free personalised income estimate for your property. No commitment, no pressure.</p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <button
                onClick={() => setShowModal(true)}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "hsl(var(--primary))", color: "#fff", border: "none", borderRadius: 8, padding: "13px 24px", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", transition: "background 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.background = "#C4C024"}
                onMouseLeave={e => e.currentTarget.style.background = "hsl(var(--primary))"}
              >
                Get my free estimate
              </button>
              <button
                onClick={handleDownload}
                style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#fff", color: "#1A1A1A", border: "1.5px solid #E5E5E5", borderRadius: 8, padding: "13px 24px", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", transition: "border-color 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.borderColor = "#1A1A1A"}
                onMouseLeave={e => e.currentTarget.style.borderColor = "#E5E5E5"}
              >
                <Download size={14} /> Download PDF checklist
              </button>
            </div>
          </div>
        </div>
      </div>

      {showModal && <EnrichmentModal mode="full" onClose={() => setShowModal(false)} onSubmitted={() => setShowModal(false)} />}
      {showDownloadGate && <ChecklistDownloadModal onClose={() => setShowDownloadGate(false)} />}

      <style>{`
        @media (max-width: 768px) {
          .checklist-hero-grid { grid-template-columns: 1fr !important; }
          .checklist-progress-hero { display: none !important; }
          .checklist-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}